import React, { useState, useEffect } from 'react';
import {
    FileSearch, Edit3, Check, X, MessageSquare, Filter, Store, Hash, Tag,
    ShieldCheck, ShieldX, AlertTriangle, Save, Calendar, Download
} from 'lucide-react';
import * as XLSX from 'xlsx';
import useOrdenacaoTabela from '../useOrdenacaoTabela';
import { categoriasDespesa } from './pettycash-contas';

// Rótulo do estado de auditoria (PENDENTE|VALIDADO|NAO_VALIDADO). Usado na
// exportação Excel e na ordenação da coluna Validação — por isso mora fora do
// componente (identidade estável) e não dentro do exportador.
const ROTULO_STATUS_AUD = { PENDENTE: 'Pendente', VALIDADO: 'Validado', NAO_VALIDADO: 'Não Validado' };

// Tipo de cada coluna ordenável (ver useOrdenacaoTabela). Fora do componente
// para a identidade ser estável e o useMemo não re-ordenar a cada render.
// STATUS_AUD_ROTULO é um campo CALCULADO na hora de ordenar (não vem da API):
// ordena o texto do estado — Não Validado → Pendente → Validado.
const TIPOS_ORDENACAO = {
    DATA_COMPRA: 'data',
    LOJA: 'texto',
    NUMERO_CUPOM: 'texto',
    QUANTIDADE: 'numero',
    DESCRICAO: 'texto',
    VALOR: 'numero',
    VALOR_AUDITORIA: 'numero',
    STATUS_AUD_ROTULO: 'texto',
};

// Mesma lista do formulário — fonte única em `pettycash-contas.js`. ⚠️ Aqui ela
// alimenta o FILTRO por categoria, que compara com a DESCRICAO gravada no banco:
// renomear conta sem atualizar os lançamentos existentes faz a categoria devolver
// zero linhas (ver o aviso no arquivo da lista).

// ── Helpers visuais ──────────────────────────────────────────────────────────
const Badge = ({ cor, texto, icone: Icone }) => {
    const cores = {
        verde:   { bg: '#dcfce7', border: '#86efac', text: '#166534' },
        laranja: { bg: '#fff7ed', border: '#fdba74', text: '#9a3412' },
        cinza:   { bg: '#f1f5f9', border: '#cbd5e1', text: '#475569' },
        vermelho:{ bg: '#fef2f2', border: '#fca5a5', text: '#991b1b' },
    };
    const c = cores[cor] || cores.cinza;
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '4px',
            padding: '3px 8px', borderRadius: '99px', fontSize: '11px',
            fontWeight: '700', backgroundColor: c.bg,
            border: `1px solid ${c.border}`, color: c.text,
            whiteSpace: 'nowrap'
        }}>
            {Icone && <Icone size={11} />}
            {texto}
        </span>
    );
};

// ── Célula inline editável ───────────────────────────────────────────────────
const InlineField = ({ label, valor, valorOriginal, tipo = 'text', opcoes = null, onChange, podeEditar }) => {
    const [editando, setEditando] = useState(false);
    const [rascunho, setRascunho] = useState(valor || '');

    const foiCorrigido = valor !== null && valor !== undefined && valor !== '';
    const exibir = foiCorrigido ? valor : (valorOriginal ?? '-');

    const confirmar = () => {
        onChange(rascunho);
        setEditando(false);
    };
    const cancelar = () => {
        setRascunho(valor || '');
        setEditando(false);
    };

    if (!podeEditar) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <span style={{ fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase', fontWeight: '600' }}>{label}</span>
                <span style={{ color: foiCorrigido ? '#ea580c' : '#0f172a', fontWeight: foiCorrigido ? '700' : '400' }}>
                    {exibir}
                </span>
                {foiCorrigido && <span style={{ fontSize: '10px', color: '#94a3b8', textDecoration: 'line-through' }}>{valorOriginal}</span>}
            </div>
        );
    }

    if (editando) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <span style={{ fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase', fontWeight: '600' }}>{label}</span>
                {opcoes ? (
                    <select
                        value={rascunho}
                        onChange={e => setRascunho(e.target.value)}
                        style={{ padding: '4px 6px', borderRadius: '5px', border: '1px solid #16a39c', fontSize: '12px', outline: 'none' }}
                        autoFocus
                    >
                        <option value="">— selecionar —</option>
                        {opcoes.map((o, i) => <option key={i} value={o.desc}>{o.desc}</option>)}
                    </select>
                ) : (
                    <input
                        type={tipo}
                        value={rascunho}
                        onChange={e => setRascunho(e.target.value)}
                        style={{ padding: '4px 6px', borderRadius: '5px', border: '1px solid #16a39c', fontSize: '12px', outline: 'none', width: '100%' }}
                        autoFocus
                    />
                )}
                <div style={{ display: 'flex', gap: '4px' }}>
                    <button onClick={confirmar} title="Confirmar" style={{ padding: '3px 8px', backgroundColor: '#16a39c', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '3px' }}>
                        <Save size={11} /> Ok
                    </button>
                    <button onClick={cancelar} title="Cancelar" style={{ padding: '3px 8px', backgroundColor: '#f1f5f9', color: '#64748b', border: '1px solid #cbd5e1', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '3px' }}>
                        <X size={11} /> Cancelar
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div
            onClick={() => { setRascunho(valor || ''); setEditando(true); }}
            title={`Clique para editar: ${label}`}
            style={{ display: 'flex', flexDirection: 'column', gap: '2px', cursor: 'pointer', padding: '3px 4px', borderRadius: '5px', transition: 'background 0.15s' }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f0fdf4'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
        >
            <span style={{ fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase', fontWeight: '600' }}>{label}</span>
            <span style={{ color: foiCorrigido ? '#ea580c' : '#0f172a', fontWeight: foiCorrigido ? '700' : '400', display: 'flex', alignItems: 'center', gap: '4px' }}>
                {exibir}
                <Edit3 size={10} color="#94a3b8" />
            </span>
            {foiCorrigido && <span style={{ fontSize: '10px', color: '#94a3b8', textDecoration: 'line-through' }}>{valorOriginal}</span>}
        </div>
    );
};

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================
const FinanceiroPettyCashAuditoria = () => {
    const [transacoes, setTransacoes] = useState([]);
    const [lojas, setLojas] = useState([]);
    const [isCarregando, setIsCarregando] = useState(true);

    // Filtros
    const [filtroLoja, setFiltroLoja] = useState('');
    const [filtroCupom, setFiltroCupom] = useState('');
    const [filtroDespesa, setFiltroDespesa] = useState('');
    const [filtroStatus, setFiltroStatus] = useState(''); // 'validado' | 'pendente' | ''
    const [filtroPeriodo, setFiltroPeriodo] = useState(''); // 'MM/AAAA'

    // Observação
    const [idEditandoObs, setIdEditandoObs] = useState(null);
    const [textoObservacao, setTextoObservacao] = useState('');

    // Linha expandida (mostrar campos de correção)
    // const [idExpandido, setIdExpandido] = useState(null); // reservado para uso futuro

    // Correções pendentes de salvar (buffer por ID)
    const [correcoes, setCorrecoes] = useState({});
    const [salvando, setSalvando] = useState({});

    // Modal "Não Validado" — lançamento em decisão + campos do mini-form.
    // Dois eixos independentes: (1) valor do lançamento; (2) falta/sobra à parte.
    const [naoValidando, setNaoValidando] = useState(null);   // transação alvo (ou null)
    // Eixo 1 — valor do lançamento no caixa (rádio: false=não alterar, true=corrigido)
    const [nvIncluir, setNvIncluir] = useState(false);
    const [nvValor, setNvValor] = useState('');
    const [nvMes, setNvMes] = useState('');                   // 'AAAA-MM' (input type="month")
    // Eixo 2 — falta/sobra lançada à parte (checkbox independente + campos)
    const [nvFaltaSobra, setNvFaltaSobra] = useState(false);
    const [nvFsValor, setNvFsValor] = useState('');
    const [nvFsMes, setNvFsMes] = useState('');               // 'AAAA-MM'
    const [nvFsSinal, setNvFsSinal] = useState('ACRESCENTAR'); // 'ACRESCENTAR' | 'TIRAR'
    const [nvSalvando, setNvSalvando] = useState(false);

    // Perfil do usuário logado
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    // ⚠️ `Operacional` (28/09/2026): acesso total ao Financeiro (nível Admin).
    const podeValidar = ['Admin', 'Financeiro', 'Auditoria', 'Gerente Adm', 'Operacional'].includes(user?.perfil);
    // Só Financeiro/Admin (e o Operacional, nível Admin) decidem DINHEIRO (marcar Não
    // Validado e escolher valor/mês que contam no caixa) — decisão da Julia, 2026-07-02.
    const podeDecidirValor = ['Admin', 'Financeiro', 'Operacional'].includes(user?.perfil);

    // Estado de auditoria em 3 valores. Fallback pro boolean legado caso o backend
    // ainda não devolva AUDITORIA_STATUS (janela entre migration e deploy).
    const getStatusAud = (t) => t.AUDITORIA_STATUS || (t.AUDITORIA_VALIDADO === 1 ? 'VALIDADO' : 'PENDENTE');

    // Converte 'DD/MM/AAAA' → 'AAAA-MM' (mês da compra, p/ default do input month)
    const mesDaCompra = (dataBr) => {
        if (!dataBr) return '';
        const [, mes, ano] = dataBr.split('/');
        return `${ano}-${mes}`;
    };

    // ── Carregamento ─────────────────────────────────────────────────────────
    useEffect(() => {
        const token = localStorage.getItem('token') || localStorage.getItem('authToken');

        fetch('/api/pettycash/lojas', { headers: { 'Authorization': `Bearer ${token}` } })
            .then(r => r.json())
            .then(d => { if (Array.isArray(d)) setLojas(d); })
            .catch(err => console.error('Erro ao carregar lojas', err));

        setIsCarregando(true);
        fetch('/api/pettycash/listar', { headers: { 'Authorization': `Bearer ${token}` } })
            .then(r => r.json())
            .then(d => { if (Array.isArray(d)) setTransacoes(d); })
            .catch(err => console.error('Erro ao carregar dados para auditoria', err))
            .finally(() => setIsCarregando(false));
    }, []);

    // ── Salvar Observação ────────────────────────────────────────────────────
    const salvarObservacao = async (id) => {
        const token = localStorage.getItem('token') || localStorage.getItem('authToken');
        try {
            const response = await fetch(`/api/pettycash/observacao/${id}`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ observacao: textoObservacao })
            });
            if (response.ok) {
                setTransacoes(prev => prev.map(t => t.ID === id ? { ...t, OBSERVACAO_AUDITORIA: textoObservacao } : t));
                setIdEditandoObs(null);
            } else {
                alert('Erro ao salvar observação.');
            }
        } catch { alert('Falha na conexão com o servidor.'); }
    };

    // ── Validar Lançamento ───────────────────────────────────────────────────
    // A falta/sobra NÃO morre junto por padrão (mudança de 2026-08-06): quando existe
    // ajuste pendente, a tela pergunta em separado o que fazer com ele. Antes o Validar
    // cancelava o ajuste calado — e apagou a falta/sobra recém-lançada na PCS. Validar
    // o lançamento e MANTER o ajuste é caso real: a despesa está correta, o ajuste
    // existe para tirar do caixa uma duplicata dela.
    const validarLancamento = async (t) => {
        const revalidando = getStatusAud(t) === 'NAO_VALIDADO';
        const msg = revalidando
            ? 'Revalidar este lançamento? O valor e a competência voltam ao ORIGINAL (desfaz a decisão do Não Validado).'
            : 'Confirmar que este lançamento está correto e validado?';
        if (!window.confirm(msg)) return;

        // Só pergunta se há ajuste VIVO e ainda PENDENTE: o /listar já não traz ajuste
        // cancelado, e ajuste 'Aprovado' é dinheiro já reposto — o backend não o derruba.
        const ajusteAtivo = t.AJUSTE_VALOR != null && t.AJUSTE_STATUS === 'Pendente';
        let cancelarAjuste = false;
        if (ajusteAtivo) {
            const v = parseFloat(t.AJUSTE_VALOR);
            cancelarAjuste = window.confirm(
                `Este lançamento tem uma falta/sobra de ${v < 0 ? '−' : '+'}R$ ${Math.abs(v).toFixed(2)} em ${t.AJUSTE_COMPETENCIA}.\n\n` +
                'Cancelar também essa falta/sobra?\n\n' +
                'OK = cancela o ajuste\n' +
                'Cancelar = valida o lançamento e MANTÉM o ajuste'
            );
        }

        const token = localStorage.getItem('token') || localStorage.getItem('authToken');
        try {
            const r = await fetch(`/api/pettycash/validar/${t.ID}`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ cancelarAjuste })
            });
            if (r.ok) {
                const agora = new Date().toLocaleString('pt-BR');
                setTransacoes(prev => prev.map(x =>
                    x.ID === t.ID ? {
                        ...x,
                        AUDITORIA_STATUS: 'VALIDADO',
                        AUDITORIA_VALIDADO: 1,
                        AUDITORIA_VALIDADO_EM: agora,
                        AUDITORIA_VALIDADO_POR_NOME: user.nome || 'Você',
                        // Espelha o backend: validar restaura os computados pro original
                        VALOR_COMPUTADO: x.VALOR,
                        COMPETENCIA: x.DATA_COMPRA ? x.DATA_COMPRA.substring(3) : x.COMPETENCIA,
                        COMPETENCIA_INPUT: mesDaCompra(x.DATA_COMPRA),
                        // Ajuste cancelado some da linha na hora. Sem isto o selo
                        // "falta/sobra ... em MM/AAAA" ficava na tela até o F5, e o
                        // operador jurava que o ajuste tinha sobrevivido ao Validar.
                        ...(cancelarAjuste && ajusteAtivo ? {
                            AJUSTE_VALOR: null,
                            AJUSTE_COMPETENCIA: null,
                            AJUSTE_COMPETENCIA_INPUT: null,
                            AJUSTE_STATUS: null,
                        } : {}),
                    } : x
                ));
            } else {
                alert('Erro ao validar lançamento.');
            }
        } catch { alert('Falha na conexão com o servidor.'); }
    };

    // ── Não Validado: abrir mini-form e confirmar decisão ────────────────────
    const abrirNaoValidado = (t) => {
        setNaoValidando(t);

        // ── Eixo 1 (valor do lançamento) ──
        // Reabrindo um já Não Validado que remanejou o valor: pré-marca "corrigido".
        const jaRemanejou = getStatusAud(t) === 'NAO_VALIDADO' && t.VALOR_COMPUTADO != null
            && parseFloat(t.VALOR_COMPUTADO) !== parseFloat(t.VALOR);
        setNvIncluir(jaRemanejou);
        setNvValor(String(jaRemanejou ? t.VALOR_COMPUTADO : (t.VALOR_AUDITORIA != null ? t.VALOR_AUDITORIA : t.VALOR)));
        setNvMes((jaRemanejou && t.COMPETENCIA_INPUT) || mesDaCompra(t.DATA_COMPRA));

        // ── Eixo 2 (falta/sobra à parte) ──
        // Diferença absoluta original × auditoria: é o default do valor da falta/sobra.
        const diff = t.VALOR_AUDITORIA != null
            ? Math.abs(parseFloat(t.VALOR) - parseFloat(t.VALOR_AUDITORIA))
            : 0;
        const temAjuste = t.AJUSTE_VALOR != null; // ajuste ativo já gravado (reabrindo)
        setNvFaltaSobra(temAjuste);
        setNvFsValor(String(temAjuste ? Math.abs(parseFloat(t.AJUSTE_VALOR)) : (diff || '')));
        setNvFsSinal(temAjuste && parseFloat(t.AJUSTE_VALOR) < 0 ? 'TIRAR' : 'ACRESCENTAR');
        setNvFsMes((temAjuste && t.AJUSTE_COMPETENCIA_INPUT) || t.COMPETENCIA_INPUT || mesDaCompra(t.DATA_COMPRA));
    };

    const confirmarNaoValidado = async () => {
        const t = naoValidando;
        if (!t) return;
        // Eixo 1
        if (nvIncluir && (!parseFloat(nvValor) || parseFloat(nvValor) <= 0)) {
            alert('Informe um valor corrigido maior que zero.');
            return;
        }
        if (nvIncluir && !nvMes) {
            alert('Informe o mês de competência.');
            return;
        }
        // Eixo 2
        if (nvFaltaSobra && (!parseFloat(nvFsValor) || parseFloat(nvFsValor) <= 0)) {
            alert('Informe o valor da falta/sobra maior que zero.');
            return;
        }
        if (nvFaltaSobra && !nvFsMes) {
            alert('Informe o mês (período) da falta/sobra.');
            return;
        }

        const token = localStorage.getItem('token') || localStorage.getItem('authToken');
        setNvSalvando(true);
        try {
            const r = await fetch(`/api/pettycash/nao-validar/${t.ID}`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    incluirValor: nvIncluir,
                    valorCorrigido: nvIncluir ? parseFloat(nvValor) : null,
                    competencia: nvIncluir ? nvMes : null,
                    faltaSobra: nvFaltaSobra
                        ? { valor: parseFloat(nvFsValor), sinal: nvFsSinal, competencia: nvFsMes }
                        : null,
                })
            });
            const data = await r.json().catch(() => ({}));
            if (r.ok) {
                const agora = new Date().toLocaleString('pt-BR');
                const [anoNv, mesNv] = nvMes.split('-');
                // Ajuste com sinal p/ o update otimista (+ acrescentar / − tirar)
                const ajusteAssinado = nvFaltaSobra
                    ? (nvFsSinal === 'TIRAR' ? -Math.abs(parseFloat(nvFsValor)) : Math.abs(parseFloat(nvFsValor)))
                    : null;
                const [anoFs, mesFs] = (nvFsMes || '').split('-');
                setTransacoes(prev => prev.map(x =>
                    x.ID === t.ID ? {
                        ...x,
                        AUDITORIA_STATUS: 'NAO_VALIDADO',
                        AUDITORIA_VALIDADO: 0,
                        AUDITORIA_VALIDADO_EM: agora,
                        AUDITORIA_VALIDADO_POR_NOME: user.nome || 'Você',
                        VALOR_COMPUTADO: nvIncluir ? parseFloat(nvValor) : x.VALOR,
                        COMPETENCIA: nvIncluir ? `${mesNv}/${anoNv}` : (x.DATA_COMPRA ? x.DATA_COMPRA.substring(3) : x.COMPETENCIA),
                        COMPETENCIA_INPUT: nvIncluir ? nvMes : mesDaCompra(x.DATA_COMPRA),
                        VALOR_AUDITORIA: nvIncluir ? parseFloat(nvValor) : x.VALOR_AUDITORIA,
                        // Ajuste (falta/sobra): reflete no pai p/ exibição inline
                        AJUSTE_VALOR: ajusteAssinado,
                        AJUSTE_COMPETENCIA: nvFaltaSobra ? `${mesFs}/${anoFs}` : null,
                        AJUSTE_COMPETENCIA_INPUT: nvFaltaSobra ? nvFsMes : null,
                        AJUSTE_STATUS: nvFaltaSobra ? 'Pendente' : null,
                    } : x
                ));
                setNaoValidando(null);
                // Estouro do teto nas competências afetadas não bloqueia — só avisa
                if (data.aviso) alert(data.aviso);
            } else {
                alert(data.error || 'Erro ao marcar como Não Validado.');
            }
        } catch { alert('Falha na conexão com o servidor.'); }
        finally { setNvSalvando(false); }
    };

    // ── Atualizar buffer de correção para um campo específico ────────────────
    const atualizarCorrecao = (id, campo, valor) => {
        setCorrecoes(prev => ({
            ...prev,
            [id]: { ...(prev[id] || {}), [campo]: valor }
        }));
    };

    // ── Salvar todas as correções de um registro ─────────────────────────────
    const salvarCorrecoes = async (transacao) => {
        const id = transacao.ID;
        const payload = correcoes[id];
        if (!payload || Object.keys(payload).length === 0) {
            alert('Nenhuma alteração para salvar.');
            return;
        }

        const token = localStorage.getItem('token') || localStorage.getItem('authToken');
        setSalvando(prev => ({ ...prev, [id]: true }));

        try {
            const r = await fetch(`/api/pettycash/corrigir/${id}`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    dataCompraAuditoria: payload.data ?? null,
                    descricaoAuditoria: payload.descricao ?? null,
                    quantidadeAuditoria: payload.quantidade ?? null,
                    numeroCupomAuditoria: payload.cupom ?? null,
                    valorAuditoria: payload.valor ?? null,
                })
            });

            if (r.ok) {
                setTransacoes(prev => prev.map(t => {
                    if (t.ID !== id) return t;
                    return {
                        ...t,
                        DATA_COMPRA_AUDITORIA: payload.data ?? t.DATA_COMPRA_AUDITORIA,
                        DESCRICAO_AUDITORIA: payload.descricao ?? t.DESCRICAO_AUDITORIA,
                        QUANTIDADE_AUDITORIA: payload.quantidade ?? t.QUANTIDADE_AUDITORIA,
                        NUMERO_CUPOM_AUDITORIA: payload.cupom ?? t.NUMERO_CUPOM_AUDITORIA,
                        VALOR_AUDITORIA: payload.valor ?? t.VALOR_AUDITORIA,
                    };
                }));
                setCorrecoes(prev => { const n = { ...prev }; delete n[id]; return n; });
            } else {
                alert('Erro ao salvar correções.');
            }
        } catch { alert('Falha na conexão com o servidor.'); }
        finally { setSalvando(prev => ({ ...prev, [id]: false })); }
    };

    // ── Filtros ───────────────────────────────────────────────────────────────
    // Períodos disponíveis derivados das transações (formato 'MM/AAAA' — substring(3) de 'DD/MM/AAAA')
    const periodosDisponiveis = Array.from(
        new Set(transacoes.map(t => t.DATA_COMPRA ? t.DATA_COMPRA.substring(3) : null).filter(Boolean))
    ).sort().reverse();

    const transacoesFiltradas = transacoes.filter(t => {
        const passaLoja = filtroLoja ? t.LOJA === filtroLoja : true;
        const passaCupom = filtroCupom
            ? (t.NUMERO_CUPOM && t.NUMERO_CUPOM.toLowerCase().includes(filtroCupom.toLowerCase()))
            : true;
        const passaDespesa = filtroDespesa ? t.DESCRICAO === filtroDespesa : true;
        const statusAud = getStatusAud(t);
        const passaStatus = filtroStatus === 'validado'
            ? statusAud === 'VALIDADO'
            : filtroStatus === 'nao-validado'
                ? statusAud === 'NAO_VALIDADO'
                : filtroStatus === 'pendente'
                    ? statusAud === 'PENDENTE'
                    : true;
        const passaPeriodo = filtroPeriodo
            ? (t.DATA_COMPRA && t.DATA_COMPRA.substring(3) === filtroPeriodo)
            : true;
        return passaLoja && passaCupom && passaDespesa && passaStatus && passaPeriodo;
    });

    // Ordenação clicável pelo cabeçalho (ver useOrdenacaoTabela). O estado de
    // auditoria entra como campo calculado porque a API devolve o código
    // (NAO_VALIDADO) e o fallback do boolean legado mora no getStatusAud.
    const transacoesOrdenaveis = transacoesFiltradas.map(t => ({
        ...t,
        STATUS_AUD_ROTULO: ROTULO_STATUS_AUD[getStatusAud(t)] || getStatusAud(t),
    }));
    const { dadosOrdenados, ordenar, indicador } = useOrdenacaoTabela(transacoesOrdenaveis, TIPOS_ORDENACAO);

    // ── Coluna "Ações": só existe enquanto houver o que fazer ─────────────────
    // Se TODAS as linhas do filtro estão validadas, a coluna inteira sai da tabela em
    // vez de virar uma faixa de traços. Correção ainda não salva mantém a ação viva
    // (o botão Salvar). ⚠️ Para Financeiro/Admin a linha validada VOLTOU a ter ação
    // desde 2026-08-06 — o "Reabrir decisão" — então para esses perfis a coluna fica.
    const temAcaoDisponivel = (t) =>
        getStatusAud(t) !== 'VALIDADO' || podeDecidirValor || Object.keys(correcoes[t.ID] || {}).length > 0;
    const mostrarColunaAcoes = podeValidar && transacoesFiltradas.some(temAcaoDisponivel);

    // ── Exportar Excel (respeita os filtros da tela — padrão do projeto) ─────
    const exportarExcel = () => {
        if (transacoesFiltradas.length === 0) {
            alert('Nenhum registro para exportar com estes filtros.');
            return;
        }
        const linhas = transacoesFiltradas.map(t => ({
            Loja: t.LOJA,
            'Data da Compra': t.DATA_COMPRA,
            'Nº Cupom': t.NUMERO_CUPOM_AUDITORIA || t.NUMERO_CUPOM || '—',
            Quantidade: t.QUANTIDADE_AUDITORIA ?? t.QUANTIDADE ?? '—',
            Despesa: t.DESCRICAO_AUDITORIA || t.DESCRICAO,
            'Conta Despesa': t.CONTA_DESPESA,
            'Valor Original (R$)': parseFloat(t.VALOR || 0),
            'Valor Auditoria (R$)': t.VALOR_AUDITORIA != null ? parseFloat(t.VALOR_AUDITORIA) : null,
            // Computado/competência = o que efetivamente conta no caixa
            'Valor Computado (R$)': parseFloat(t.VALOR_COMPUTADO ?? t.VALOR ?? 0),
            'Competência': t.COMPETENCIA || (t.DATA_COMPRA ? t.DATA_COMPRA.substring(3) : '—'),
            // Falta/sobra lançada à parte (movimento próprio; sinal + acrescenta / − tira)
            'Falta/Sobra (R$)': t.AJUSTE_VALOR != null ? parseFloat(t.AJUSTE_VALOR) : null,
            'Falta/Sobra Período': t.AJUSTE_VALOR != null ? (t.AJUSTE_COMPETENCIA || '—') : '—',
            'Status Reposição': t.STATUS,
            'Status Auditoria': ROTULO_STATUS_AUD[getStatusAud(t)] || getStatusAud(t),
            'Validado/Decidido por': t.AUDITORIA_VALIDADO_POR_NOME || '—',
            'Em': t.AUDITORIA_VALIDADO_EM || '—',
            'Observação': t.OBSERVACAO_AUDITORIA || '',
        }));
        const ws = XLSX.utils.json_to_sheet(linhas);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Auditoria Petty Cash');
        // Nome do arquivo com a data de hoje (AAAA-MM-DD) para versionar exportações.
        const hoje = new Date().toISOString().slice(0, 10);
        XLSX.writeFile(wb, `PettyCash_Auditoria_${hoje}.xlsx`);
    };

    const inputStyle = {
        padding: '10px 12px 10px 36px', borderRadius: '8px', border: '1px solid #cbd5e1',
        outline: 'none', fontSize: '14px', width: '200px'
    };

    const thStyle = { padding: '12px 14px', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' };
    // Cabeçalho clicável: cursor de mão e sem seleção de texto ao clicar repetido.
    const thOrdenavel = { ...thStyle, cursor: 'pointer', userSelect: 'none' };
    // Metade clicável de um cabeçalho composto (ex.: "Data / Loja").
    const spanOrdenavel = { cursor: 'pointer', userSelect: 'none' };
    const tdStyle = { padding: '14px', verticalAlign: 'top', borderBottom: '1px solid #f1f5f9' };

    // ── Totalizadores rápidos ─────────────────────────────────────────────────
    const totalValidados = transacoes.filter(t => getStatusAud(t) === 'VALIDADO').length;
    const totalNaoValidados = transacoes.filter(t => getStatusAud(t) === 'NAO_VALIDADO').length;
    const totalCorrigidos = transacoes.filter(t =>
        t.DATA_COMPRA_AUDITORIA || t.DESCRICAO_AUDITORIA || t.QUANTIDADE_AUDITORIA ||
        t.NUMERO_CUPOM_AUDITORIA || t.VALOR_AUDITORIA
    ).length;

    return (
        <div>
            {/* ── CARDS RESUMO ──────────────────────────────────────────────── */}
            <div style={{ display: 'flex', gap: '16px', marginBottom: '20px', flexWrap: 'wrap' }}>
                {[
                    { label: 'Total Lançamentos', valor: transacoes.length, cor: '#0ea5e9', bg: '#f0f9ff' },
                    { label: 'Validados ✔', valor: totalValidados, cor: '#16a34a', bg: '#f0fdf4' },
                    { label: 'Não Validados', valor: totalNaoValidados, cor: '#dc2626', bg: '#fef2f2' },
                    { label: 'Com Correções', valor: totalCorrigidos, cor: '#ea580c', bg: '#fff7ed' },
                    { label: 'Pendentes', valor: transacoes.length - totalValidados - totalNaoValidados, cor: '#64748b', bg: '#f8fafc' },
                ].map((c, i) => (
                    <div key={i} style={{ flex: '1 1 160px', backgroundColor: c.bg, borderRadius: '10px', padding: '14px 18px', border: `1px solid ${c.cor}22` }}>
                        <div style={{ fontSize: '11px', color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{c.label}</div>
                        <div style={{ fontSize: '26px', fontWeight: '800', color: c.cor, marginTop: '4px' }}>{c.valor}</div>
                    </div>
                ))}
            </div>

            {/* ── BARRA DE FILTROS ──────────────────────────────────────────── */}
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '20px', marginBottom: '20px', backgroundColor: '#fff', padding: '16px 20px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#64748b' }}>
                    <Filter size={18} /> <span style={{ fontWeight: '600', fontSize: '14px' }}>Filtros:</span>
                </div>

                {/* Loja */}
                <div style={{ position: 'relative' }}>
                    <Store size={16} color="#94a3b8" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
                    <select value={filtroLoja} onChange={e => setFiltroLoja(e.target.value)} style={inputStyle}>
                        <option value="">Todas as Lojas</option>
                        {lojas.map((l, i) => <option key={i} value={l.NOME_LOJA}>{l.NOME_LOJA}</option>)}
                    </select>
                </div>

                {/* Cupom */}
                <div style={{ position: 'relative' }}>
                    <Hash size={16} color="#94a3b8" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
                    <input type="text" placeholder="Nº do Cupom..." value={filtroCupom} onChange={e => setFiltroCupom(e.target.value)} style={inputStyle} />
                </div>

                {/* Despesa */}
                <div style={{ position: 'relative' }}>
                    <Tag size={16} color="#94a3b8" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
                    <select value={filtroDespesa} onChange={e => setFiltroDespesa(e.target.value)} style={inputStyle}>
                        <option value="">Todas as Despesas</option>
                        {categoriasDespesa.map((cat, i) => <option key={i} value={cat.desc}>{cat.desc}</option>)}
                    </select>
                </div>

                {/* Período (Mês/Ano) */}
                <div style={{ position: 'relative' }}>
                    <Calendar size={16} color="#94a3b8" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
                    <select value={filtroPeriodo} onChange={e => setFiltroPeriodo(e.target.value)} style={inputStyle}>
                        <option value="">Todo o Período</option>
                        {periodosDisponiveis.map((p, i) => <option key={i} value={p}>{p}</option>)}
                    </select>
                </div>

                {/* Status validação */}
                <div style={{ position: 'relative' }}>
                    <ShieldCheck size={16} color="#94a3b8" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
                    <select value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)} style={inputStyle}>
                        <option value="">Todos os Status</option>
                        <option value="validado">Validados ✔</option>
                        <option value="nao-validado">Não Validados</option>
                        <option value="pendente">Pendentes</option>
                    </select>
                </div>
            </div>

            {/* ── TABELA ────────────────────────────────────────────────────── */}
            <div style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', borderBottom: '1px solid #e2e8f0', paddingBottom: '14px', marginBottom: '16px' }}>
                    <MessageSquare size={20} color="#16a39c" />
                    <h3 style={{ margin: 0, fontSize: '17px', color: '#0f172a' }}>Registros e Auditoria</h3>
                    <span style={{ marginLeft: 'auto', fontSize: '13px', color: '#94a3b8' }}>
                        {transacoesFiltradas.length} registro(s) exibido(s)
                    </span>
                    <button
                        onClick={exportarExcel}
                        title="Exportar os registros filtrados para Excel"
                        style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', backgroundColor: '#16a39c', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', whiteSpace: 'nowrap' }}
                    >
                        <Download size={15} /> Exportar Excel
                    </button>
                </div>

                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: '1100px' }}>
                        <thead style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', backgroundColor: '#f8fafc' }}>
                            <tr>
                                {/* Colunas compostas (Data/Loja, Cupom/Qtd) têm as DUAS partes
                                    clicáveis — cada metade ordena pelo seu próprio dado, senão
                                    metade da informação da coluna ficaria sem ordenação. */}
                                <th style={thStyle}>
                                    <span onClick={() => ordenar('DATA_COMPRA')} style={spanOrdenavel}>Data{indicador('DATA_COMPRA')}</span>
                                    {' / '}
                                    <span onClick={() => ordenar('LOJA')} style={spanOrdenavel}>Loja{indicador('LOJA')}</span>
                                </th>
                                <th style={thStyle}>
                                    <span onClick={() => ordenar('NUMERO_CUPOM')} style={spanOrdenavel}>Nº Cupom{indicador('NUMERO_CUPOM')}</span>
                                    {' / '}
                                    <span onClick={() => ordenar('QUANTIDADE')} style={spanOrdenavel}>Qtd{indicador('QUANTIDADE')}</span>
                                </th>
                                <th onClick={() => ordenar('DESCRICAO')} style={thOrdenavel}>Despesa{indicador('DESCRICAO')}</th>
                                <th onClick={() => ordenar('VALOR')} style={{ ...thOrdenavel, textAlign: 'right' }}>Valor Original{indicador('VALOR')}</th>
                                <th onClick={() => ordenar('VALOR_AUDITORIA')} style={{ ...thOrdenavel, textAlign: 'right' }}>Valor Auditoria{indicador('VALOR_AUDITORIA')}</th>
                                <th style={{ ...thStyle, textAlign: 'center' }}>Arquivo</th>
                                <th style={{ ...thStyle, width: '25%' }}>Observação</th>
                                <th onClick={() => ordenar('STATUS_AUD_ROTULO')} style={{ ...thOrdenavel, textAlign: 'center' }}>Validação{indicador('STATUS_AUD_ROTULO')}</th>
                                {mostrarColunaAcoes && <th style={{ ...thStyle, textAlign: 'center' }}>Ações</th>}
                            </tr>
                        </thead>
                        <tbody style={{ fontSize: '13px', color: '#475569' }}>
                            {isCarregando ? (
                                <tr><td colSpan={mostrarColunaAcoes ? 9 : 8} style={{ padding: '32px', textAlign: 'center', color: '#94a3b8' }}>Carregando transações...</td></tr>
                            ) : transacoesFiltradas.length === 0 ? (
                                <tr><td colSpan={mostrarColunaAcoes ? 9 : 8} style={{ padding: '32px', textAlign: 'center', color: '#94a3b8' }}>Nenhuma transação encontrada com estes filtros.</td></tr>
                            ) : (
                                dadosOrdenados.map((t) => {
                                    const temCorrecao = !!(
                                        t.DATA_COMPRA_AUDITORIA || t.DESCRICAO_AUDITORIA ||
                                        t.QUANTIDADE_AUDITORIA || t.NUMERO_CUPOM_AUDITORIA || t.VALOR_AUDITORIA
                                    );
                                    const statusAud = getStatusAud(t);
                                    const validado = statusAud === 'VALIDADO';
                                    const naoValidado = statusAud === 'NAO_VALIDADO';
                                    // Marca visual de decisão de caixa: o computado difere do original
                                    const valorRemanejado = naoValidado && t.VALOR_COMPUTADO != null && parseFloat(t.VALOR_COMPUTADO) !== parseFloat(t.VALOR);
                                    const bufferLocal = correcoes[t.ID] || {};
                                    const temBufferPendente = Object.keys(bufferLocal).length > 0;

                                    const rowBg = validado
                                        ? '#f0fdf4'
                                        : naoValidado
                                            ? '#fef2f2'
                                            : temCorrecao
                                                ? '#fffbeb'
                                                : '#ffffff';

                                    return (
                                        <React.Fragment key={t.ID}>
                                            <tr style={{ backgroundColor: rowBg, transition: 'background 0.2s' }}>
                                                {/* Data / Loja */}
                                                <td style={tdStyle}>
                                                    {podeValidar ? (
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                            <InlineField
                                                                label="Data"
                                                                valor={bufferLocal.data ?? (t.DATA_COMPRA_AUDITORIA || '')}
                                                                valorOriginal={t.DATA_COMPRA}
                                                                tipo="date"
                                                                onChange={v => atualizarCorrecao(t.ID, 'data', v)}
                                                                podeEditar={podeValidar}
                                                            />
                                                            <span style={{ fontWeight: '600', color: '#0f172a' }}>{t.LOJA}</span>
                                                        </div>
                                                    ) : (
                                                        <>
                                                            <span style={{ fontWeight: '600', color: '#0f172a' }}>{t.LOJA}</span><br />
                                                            <span style={{ fontSize: '12px', color: '#64748b' }}>
                                                                {t.DATA_COMPRA_AUDITORIA ? (
                                                                    <><span style={{ color: '#ea580c', fontWeight: '700' }}>{t.DATA_COMPRA_AUDITORIA}</span> <span style={{ textDecoration: 'line-through' }}>{t.DATA_COMPRA}</span></>
                                                                ) : t.DATA_COMPRA}
                                                            </span>
                                                        </>
                                                    )}
                                                </td>

                                                {/* Nº Cupom / Qtd */}
                                                <td style={tdStyle}>
                                                    {podeValidar ? (
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                            <InlineField
                                                                label="Nº Cupom"
                                                                valor={bufferLocal.cupom ?? (t.NUMERO_CUPOM_AUDITORIA || '')}
                                                                valorOriginal={t.NUMERO_CUPOM || '-'}
                                                                onChange={v => atualizarCorrecao(t.ID, 'cupom', v)}
                                                                podeEditar={podeValidar}
                                                            />
                                                            <InlineField
                                                                label="Quantidade"
                                                                valor={bufferLocal.quantidade ?? (t.QUANTIDADE_AUDITORIA !== null && t.QUANTIDADE_AUDITORIA !== undefined ? String(t.QUANTIDADE_AUDITORIA) : '')}
                                                                valorOriginal={t.QUANTIDADE ?? '-'}
                                                                tipo="number"
                                                                onChange={v => atualizarCorrecao(t.ID, 'quantidade', v)}
                                                                podeEditar={podeValidar}
                                                            />
                                                        </div>
                                                    ) : (
                                                        <>
                                                            <span style={{ fontWeight: '500' }}>
                                                                {t.NUMERO_CUPOM_AUDITORIA ? (
                                                                    <><span style={{ color: '#ea580c' }}>{t.NUMERO_CUPOM_AUDITORIA}</span> <span style={{ fontSize: '11px', textDecoration: 'line-through', color: '#94a3b8' }}>{t.NUMERO_CUPOM}</span></>
                                                                ) : (t.NUMERO_CUPOM || '-')}
                                                            </span><br />
                                                            <span style={{ fontSize: '12px', color: '#64748b' }}>Qtd: {t.QUANTIDADE_AUDITORIA ?? t.QUANTIDADE ?? '-'}</span>
                                                        </>
                                                    )}
                                                </td>

                                                {/* Despesa */}
                                                <td style={tdStyle}>
                                                    {podeValidar ? (
                                                        <InlineField
                                                            label="Tipo de Despesa"
                                                            valor={bufferLocal.descricao ?? (t.DESCRICAO_AUDITORIA || '')}
                                                            valorOriginal={t.DESCRICAO}
                                                            opcoes={categoriasDespesa}
                                                            onChange={v => atualizarCorrecao(t.ID, 'descricao', v)}
                                                            podeEditar={podeValidar}
                                                        />
                                                    ) : (
                                                        t.DESCRICAO_AUDITORIA ? (
                                                            <>
                                                                <span style={{ color: '#ea580c', fontWeight: '700' }}>{t.DESCRICAO_AUDITORIA}</span><br />
                                                                <span style={{ fontSize: '11px', textDecoration: 'line-through', color: '#94a3b8' }}>{t.DESCRICAO}</span>
                                                            </>
                                                        ) : t.DESCRICAO
                                                    )}
                                                </td>

                                                {/* Valor Original (somente leitura) */}
                                                <td style={{ ...tdStyle, textAlign: 'right' }}>
                                                    <span style={{ fontWeight: '700', color: '#16a39c', fontSize: '14px' }}>
                                                        R$ {parseFloat(t.VALOR).toFixed(2)}
                                                    </span>
                                                    <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '2px' }}>não editável</div>
                                                </td>

                                                {/* Valor Auditoria */}
                                                <td style={{ ...tdStyle, textAlign: 'right' }}>
                                                    {podeValidar ? (
                                                        <InlineField
                                                            label="Valor Audit."
                                                            valor={bufferLocal.valor ?? (t.VALOR_AUDITORIA !== null && t.VALOR_AUDITORIA !== undefined ? String(t.VALOR_AUDITORIA) : '')}
                                                            valorOriginal={null}
                                                            tipo="number"
                                                            onChange={v => atualizarCorrecao(t.ID, 'valor', v)}
                                                            podeEditar={podeValidar}
                                                        />
                                                    ) : (
                                                        t.VALOR_AUDITORIA != null
                                                            ? <span style={{ fontWeight: '700', color: '#ea580c' }}>R$ {parseFloat(t.VALOR_AUDITORIA).toFixed(2)}</span>
                                                            : <span style={{ color: '#cbd5e1' }}>—</span>
                                                    )}
                                                </td>

                                                {/* Arquivo */}
                                                <td style={{ ...tdStyle, textAlign: 'center' }}>
                                                    {t.CUPOM_URL ? (
                                                        <a href={t.CUPOM_URL} target="_blank" rel="noopener noreferrer"
                                                            style={{ display: 'inline-flex', padding: '5px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', color: '#16a39c', textDecoration: 'none', alignItems: 'center', gap: '5px', fontWeight: '600', backgroundColor: '#fff', fontSize: '12px' }}>
                                                            <FileSearch size={13} /> Ver
                                                        </a>
                                                    ) : <span style={{ color: '#cbd5e1' }}>—</span>}
                                                </td>

                                                {/* Observação */}
                                                <td style={tdStyle}>
                                                    {idEditandoObs === t.ID ? (
                                                        <div style={{ display: 'flex', gap: '6px', alignItems: 'flex-start' }}>
                                                            <textarea
                                                                value={textoObservacao}
                                                                onChange={e => setTextoObservacao(e.target.value)}
                                                                placeholder="Observação do auditor..."
                                                                style={{ flex: 1, minHeight: '55px', padding: '6px', borderRadius: '5px', border: '1px solid #16a39c', outline: 'none', fontSize: '12px', resize: 'vertical' }}
                                                            />
                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                                                <button onClick={() => salvarObservacao(t.ID)} style={{ padding: '5px', backgroundColor: '#16a39c', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }} title="Salvar"><Check size={14} /></button>
                                                                <button onClick={() => setIdEditandoObs(null)} style={{ padding: '5px', backgroundColor: '#f1f5f9', color: '#64748b', border: '1px solid #cbd5e1', borderRadius: '4px', cursor: 'pointer' }} title="Cancelar"><X size={14} /></button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div
                                                            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px', padding: '6px 8px', backgroundColor: t.OBSERVACAO_AUDITORIA ? '#fefce8' : '#f8fafc', border: t.OBSERVACAO_AUDITORIA ? '1px solid #fde68a' : '1px dashed #cbd5e1', borderRadius: '6px', cursor: podeValidar ? 'pointer' : 'default' }}
                                                            onClick={podeValidar ? () => { setIdEditandoObs(t.ID); setTextoObservacao(t.OBSERVACAO_AUDITORIA || ''); } : undefined}
                                                        >
                                                            <span style={{ flex: 1, fontStyle: t.OBSERVACAO_AUDITORIA ? 'normal' : 'italic', color: t.OBSERVACAO_AUDITORIA ? '#854d0e' : '#94a3b8', fontSize: '12px' }}>
                                                                {t.OBSERVACAO_AUDITORIA || 'Nenhuma observação.'}
                                                            </span>
                                                            {podeValidar && <Edit3 size={13} color="#94a3b8" />}
                                                        </div>
                                                    )}
                                                </td>

                                                {/* Badge Validação */}
                                                <td style={{ ...tdStyle, textAlign: 'center' }}>
                                                    {validado ? (
                                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                                                            <Badge cor="verde" texto="✔ Validado" icone={ShieldCheck} />
                                                            <span style={{ fontSize: '10px', color: '#94a3b8' }}>{t.AUDITORIA_VALIDADO_EM}</span>
                                                            {t.AUDITORIA_VALIDADO_POR_NOME && (
                                                                <span style={{ fontSize: '10px', color: '#64748b' }}>por {t.AUDITORIA_VALIDADO_POR_NOME}</span>
                                                            )}
                                                        </div>
                                                    ) : naoValidado ? (
                                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                                                            <Badge cor="vermelho" texto="✖ Não Validado" icone={ShieldX} />
                                                            <span style={{ fontSize: '10px', color: '#94a3b8' }}>{t.AUDITORIA_VALIDADO_EM}</span>
                                                            {t.AUDITORIA_VALIDADO_POR_NOME && (
                                                                <span style={{ fontSize: '10px', color: '#64748b' }}>por {t.AUDITORIA_VALIDADO_POR_NOME}</span>
                                                            )}
                                                            {/* O que a decisão fez com o caixa */}
                                                            <span style={{ fontSize: '10px', fontWeight: '700', color: valorRemanejado ? '#b91c1c' : '#64748b' }}>
                                                                {valorRemanejado
                                                                    ? `conta R$ ${parseFloat(t.VALOR_COMPUTADO).toFixed(2)} em ${t.COMPETENCIA}`
                                                                    : 'sem alteração no caixa'}
                                                            </span>
                                                            {/* Falta/sobra lançada à parte (movimento próprio no caixa) */}
                                                            {t.AJUSTE_VALOR != null && (
                                                                <span style={{ fontSize: '10px', fontWeight: '700', color: parseFloat(t.AJUSTE_VALOR) < 0 ? '#b45309' : '#0369a1', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '5px', padding: '1px 6px' }}>
                                                                    falta/sobra {parseFloat(t.AJUSTE_VALOR) < 0 ? '−' : '+'}R$ {Math.abs(parseFloat(t.AJUSTE_VALOR)).toFixed(2)} em {t.AJUSTE_COMPETENCIA}
                                                                </span>
                                                            )}
                                                        </div>
                                                    ) : temCorrecao ? (
                                                        <Badge cor="laranja" texto="Corrigido" icone={AlertTriangle} />
                                                    ) : (
                                                        <Badge cor="cinza" texto="Pendente" />
                                                    )}
                                                </td>

                                                {/* Ações (só enquanto houver o que fazer em alguma linha) */}
                                                {mostrarColunaAcoes && (
                                                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                                                            {/* Botão Validar (vira "Revalidar" num Não Validado — restaura o original) */}
                                                            {!validado && (
                                                                <button
                                                                    onClick={() => validarLancamento(t)}
                                                                    style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '5px 10px', backgroundColor: '#16a39c', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' }}
                                                                    title={naoValidado ? 'Revalidar (restaura valor/competência originais)' : 'Confirmar que o lançamento está correto'}
                                                                >
                                                                    <ShieldCheck size={13} /> {naoValidado ? 'Revalidar' : 'Validar'}
                                                                </button>
                                                            )}

                                                            {/* Botão Não Validado — só Financeiro/Admin (decide dinheiro).
                                                                Já não-validado: vira "Editar decisão" (reabre o modal p/ ajustar
                                                                valor/competência e a falta/sobra sem precisar revalidar antes).
                                                                Em 2026-07-31 este botão passou a SUMIR no lançamento validado —
                                                                validado é decisão tomada, e o botão vermelho ao lado do selo verde
                                                                confundia. Só que isso deixou a linha validada sem NENHUMA saída:
                                                                desfazer uma validação errada virou trabalho de Admin por fora da
                                                                tela — e foi o que travou o conserto do caso da PCS (2026-08-06). */}
                                                            {podeDecidirValor && !validado && (
                                                                <button
                                                                    onClick={() => abrirNaoValidado(t)}
                                                                    style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '5px 10px', backgroundColor: '#fff', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' }}
                                                                    title={naoValidado ? 'Editar a decisão (valor/competência e falta/sobra)' : 'Marcar como Não Validado e decidir valor/competência no caixa'}
                                                                >
                                                                    <ShieldX size={13} /> {naoValidado ? 'Editar decisão' : 'Não Validado'}
                                                                </button>
                                                            )}

                                                            {/* Reabrir decisão (2026-08-06) — o caminho de volta do VALIDADO.
                                                                Discreto de propósito: texto cinza sem moldura, para não competir
                                                                com o selo verde (era esse o incômodo de 31/07). Só Financeiro/Admin,
                                                                como o "Não Validado" — é a mesma porta, e ela decide dinheiro. */}
                                                            {podeDecidirValor && validado && (
                                                                <button
                                                                    onClick={() => abrirNaoValidado(t)}
                                                                    style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '2px 6px', backgroundColor: 'transparent', color: '#94a3b8', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: '600', whiteSpace: 'nowrap', textDecoration: 'underline' }}
                                                                    title="Reabrir a decisão deste lançamento (valor/competência e falta/sobra)"
                                                                >
                                                                    <ShieldX size={11} /> Reabrir decisão
                                                                </button>
                                                            )}

                                                            {/* Salvar correções pendentes no buffer */}
                                                            {temBufferPendente && (
                                                                <button
                                                                    onClick={() => salvarCorrecoes(t)}
                                                                    disabled={salvando[t.ID]}
                                                                    style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '5px 10px', backgroundColor: '#ea580c', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap', opacity: salvando[t.ID] ? 0.6 : 1 }}
                                                                    title="Salvar correções pendentes"
                                                                >
                                                                    <Save size={13} /> {salvando[t.ID] ? 'Salvando...' : 'Salvar Correções'}
                                                                </button>
                                                            )}

                                                            {/* Validado e sem correção pendente: nada a fazer. Um traço no
                                                                lugar da célula vazia deixa claro que é o fim da linha, e não
                                                                um botão que não carregou. */}
                                                            {validado && !temBufferPendente && (
                                                                <span title="Lançamento validado — nada mais a fazer aqui." style={{ color: '#cbd5e1', fontSize: '13px' }}>—</span>
                                                            )}
                                                        </div>
                                                    </td>
                                                )}
                                            </tr>
                                        </React.Fragment>
                                    );
                                })
                            )}
                        </tbody>

                        {/* ── LINHA DE TOTAIS ──────────────────────────────── */}
                        {!isCarregando && transacoesFiltradas.length > 0 && (() => {
                            const totalValorOriginal = transacoesFiltradas.reduce(
                                (acc, t) => acc + parseFloat(t.VALOR || 0), 0
                            );
                            // Total COMPUTADO = o que efetivamente conta no caixa (VALOR_COMPUTADO;
                            // cai pro VALOR original se o backend ainda não devolver o campo)
                            const totalValorComputado = transacoesFiltradas.reduce(
                                (acc, t) => acc + parseFloat(t.VALOR_COMPUTADO ?? t.VALOR ?? 0), 0
                            );
                            // 8 colunas fixas + a de Ações, quando ela está na tela
                            const colSpanAcoes = mostrarColunaAcoes ? 9 : 8;

                            return (
                                <tfoot>
                                    <tr style={{ backgroundColor: '#f0fdf4', borderTop: '2px solid #86efac' }}>
                                        {/* Data/Loja, Nº Cupom/Qtd, Despesa — células vazias */}
                                        <td style={{ padding: '14px', fontWeight: '800', fontSize: '13px', color: '#166534', textAlign: 'right', whiteSpace: 'nowrap' }} colSpan={3}>
                                            TOTAL ({transacoesFiltradas.length} registro{transacoesFiltradas.length !== 1 ? 's' : ''})
                                        </td>

                                        {/* Valor Original */}
                                        <td style={{ padding: '14px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                            <span style={{ fontWeight: '800', fontSize: '15px', color: '#16a39c' }}>
                                                R$ {totalValorOriginal.toFixed(2).replace('.', ',')}
                                            </span>
                                        </td>

                                        {/* Total computado — o que efetivamente conta no caixa */}
                                        <td style={{ padding: '14px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                            <span style={{ fontWeight: '800', fontSize: '15px', color: '#ea580c' }}>
                                                R$ {totalValorComputado.toFixed(2).replace('.', ',')}
                                            </span>
                                            <div style={{ fontSize: '10px', color: '#94a3b8' }}>conta no caixa</div>
                                        </td>

                                        {/* Arquivo + Observação + Validação + Ações */}
                                        <td colSpan={colSpanAcoes - 5} style={{ padding: '14px' }} />
                                    </tr>
                                </tfoot>
                            );
                        })()}
                    </table>
                </div>
            </div>

            {/* ── MODAL: decisão do "Não Validado" (só Financeiro/Admin chega aqui) ── */}
            {naoValidando && (
                <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
                    <div style={{ backgroundColor: '#fff', padding: '30px', borderRadius: '14px', width: '90%', maxWidth: '480px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                            <div style={{ width: '36px', height: '36px', borderRadius: '50%', backgroundColor: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <ShieldX size={18} color="#dc2626" />
                            </div>
                            <h3 style={{ margin: 0, color: '#b91c1c', fontSize: '18px', fontWeight: '700' }}>
                                {getStatusAud(naoValidando) === 'NAO_VALIDADO' ? 'Editar decisão do Não Validado' : 'Marcar como Não Validado'}
                            </h3>
                        </div>

                        {/* Resumo do lançamento em decisão */}
                        <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '12px 16px', marginBottom: '16px', fontSize: '13px', color: '#334155' }}>
                            <div><strong>Loja:</strong> {naoValidando.LOJA} · <strong>Data:</strong> {naoValidando.DATA_COMPRA}</div>
                            <div><strong>Despesa:</strong> {naoValidando.DESCRICAO}</div>
                            <div><strong>Valor original:</strong> R$ {parseFloat(naoValidando.VALOR).toFixed(2)}</div>
                        </div>

                        {/* ── EIXO 1: o que o LANÇAMENTO vale no caixa (escolha única) ── */}
                        <p style={{ color: '#334155', fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 8px 0' }}>
                            Valor do lançamento no caixa
                        </p>

                        {/* Opção 1: não mexe no valor do lançamento */}
                        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '12px', border: `2px solid ${!nvIncluir ? '#16a39c' : '#e2e8f0'}`, borderRadius: '10px', cursor: 'pointer', marginBottom: '10px' }}>
                            <input type="radio" checked={!nvIncluir} onChange={() => setNvIncluir(false)} style={{ marginTop: '2px' }} />
                            <span style={{ fontSize: '13px', color: '#334155' }}>
                                <strong>Não alterar o valor no Petty Cash</strong><br />
                                <span style={{ color: '#64748b' }}>O lançamento fica marcado como Não Validado, mas continua contando R$ {parseFloat(naoValidando.VALOR).toFixed(2)} no mês original.</span>
                            </span>
                        </label>

                        {/* Opção 2: valor corrigido substitui o original, no mês escolhido */}
                        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '12px', border: `2px solid ${nvIncluir ? '#16a39c' : '#e2e8f0'}`, borderRadius: '10px', cursor: 'pointer', marginBottom: '16px' }}>
                            <input type="radio" checked={nvIncluir} onChange={() => setNvIncluir(true)} style={{ marginTop: '2px' }} />
                            <span style={{ fontSize: '13px', color: '#334155', flex: 1 }}>
                                <strong>Incluir valor corrigido no Petty Cash</strong><br />
                                <span style={{ color: '#64748b' }}>O valor abaixo substitui o original e passa a contar no mês escolhido.</span>
                                {nvIncluir && (
                                    <span style={{ display: 'flex', gap: '10px', marginTop: '10px', flexWrap: 'wrap' }}>
                                        <span style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                            <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600', textTransform: 'uppercase' }}>Valor (R$)</span>
                                            <input type="number" step="0.01" min="0.01" value={nvValor} onChange={e => setNvValor(e.target.value)}
                                                style={{ padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', width: '120px', outline: 'none' }} />
                                        </span>
                                        <span style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                            <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600', textTransform: 'uppercase' }}>Mês (competência)</span>
                                            <input type="month" value={nvMes} onChange={e => setNvMes(e.target.value)}
                                                style={{ padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', outline: 'none' }} />
                                        </span>
                                    </span>
                                )}
                            </span>
                        </label>

                        {/* ── EIXO 2: falta/sobra à parte (independente — combina com qualquer opção acima) ── */}
                        <p style={{ color: '#334155', fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 8px 0' }}>
                            Falta / Sobra (opcional)
                        </p>
                        <div style={{ padding: '12px', border: `2px solid ${nvFaltaSobra ? '#16a39c' : '#e2e8f0'}`, borderRadius: '10px', marginBottom: '16px' }}>
                            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer' }}>
                                <input type="checkbox" checked={nvFaltaSobra} onChange={e => setNvFaltaSobra(e.target.checked)} style={{ marginTop: '2px' }} />
                                <span style={{ fontSize: '13px', color: '#334155' }}>
                                    <strong>Registrar a falta/sobra à parte</strong><br />
                                    <span style={{ color: '#64748b' }}>
                                        Lança a diferença como um movimento próprio na competência escolhida — conta no saldo, no limite e no saque, sem mexer no valor do lançamento acima.
                                        {naoValidando.VALOR_AUDITORIA != null && (
                                            <> Diferença original × auditoria: <strong>R$ {Math.abs(parseFloat(naoValidando.VALOR) - parseFloat(naoValidando.VALOR_AUDITORIA)).toFixed(2)}</strong>.</>
                                        )}
                                    </span>
                                </span>
                            </label>
                            {nvFaltaSobra && (
                                <div style={{ display: 'flex', gap: '10px', marginTop: '12px', flexWrap: 'wrap', paddingLeft: '28px' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                        <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600', textTransform: 'uppercase' }}>Valor (R$)</span>
                                        <input type="number" step="0.01" min="0.01" value={nvFsValor} onChange={e => setNvFsValor(e.target.value)}
                                            style={{ padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', width: '120px', outline: 'none' }} />
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                        <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600', textTransform: 'uppercase' }}>Período</span>
                                        <input type="month" value={nvFsMes} onChange={e => setNvFsMes(e.target.value)}
                                            style={{ padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', outline: 'none' }} />
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                        <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600', textTransform: 'uppercase' }}>Movimento</span>
                                        <select value={nvFsSinal} onChange={e => setNvFsSinal(e.target.value)}
                                            style={{ padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', outline: 'none', backgroundColor: '#fff' }}>
                                            <option value="ACRESCENTAR">Acrescentar (+)</option>
                                            <option value="TIRAR">Tirar (−)</option>
                                        </select>
                                    </div>
                                </div>
                            )}
                        </div>

                        <p style={{ color: '#94a3b8', fontSize: '12px', margin: '0 0 16px 0' }}>
                            Se o mês escolhido estourar o limite da loja, o sistema <strong>avisa mas não bloqueia</strong>. A decisão pode ser desfeita depois com "Revalidar".
                        </p>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                            <button onClick={() => setNaoValidando(null)} disabled={nvSalvando}
                                style={{ padding: '10px 18px', backgroundColor: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}>
                                Cancelar
                            </button>
                            <button onClick={confirmarNaoValidado} disabled={nvSalvando}
                                style={{ padding: '10px 18px', backgroundColor: nvSalvando ? '#fca5a5' : '#dc2626', color: '#fff', border: 'none', borderRadius: '8px', cursor: nvSalvando ? 'not-allowed' : 'pointer', fontWeight: '700', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <ShieldX size={15} /> {nvSalvando ? 'Salvando...' : 'Confirmar Não Validado'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FinanceiroPettyCashAuditoria;